import { IFilterConfig } from '../or-filtered-grid.types';
import { IHousePlanHit } from '@/lib/search/types';
import { EIcons } from '@/components/atoms/at-icon/icons';

export const fetchExteriorStylesFacets = async () => {
  const request = await fetch('/algolia/facets');
  if (!request.ok) {
    throw new Error('Failed to fetch exterior style facets');
  }
  const facets = await request.json();
  return facets;
};

export function getPlansFilterConfig(
  exteriorStyleOptions: { label: string; value: string }[],
): IFilterConfig<IHousePlanHit>[] {
  return [
    {
      key: 'ExteriorStyle',
      label: 'Exterior Style',
      type: 'select',
      options: exteriorStyleOptions,
      default: '',
      multi: true,
      icon: EIcons.EXTERIOR,
      filterFn: (item, value) => {
        // Accept all empty states for "no filter"
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return true;
        }
        const itemValue = item.ExteriorStyle?.ExteriorStyleCategoryId;
        if (Array.isArray(value)) {
          return value.includes(itemValue.toString());
        }
        return itemValue === +value;
      },
    },
    {
      key: 'Price',
      label: 'Price',
      type: 'select',
      multi: true,
      icon: EIcons.PRICE,
      options: [
        { label: 'Under $250K', value: 'Under $250K' },
        { label: '$250K – $500K', value: '$250K – $500K' },
        { label: '$500K – $750K', value: '$500K – $750K' },
        { label: '$750K – $1M', value: '$750K – $1M' },
        { label: 'Over $1M', value: 'Over $1M' },
      ],
      default: '',
      filterFn: (item, value) => {
        const price = item?.FloorPlans?.[0]?.BasePrice ?? 0;
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        const match = (v: string) => {
          if (v === 'Under $250K') return price >= 0 && price < 250000;
          if (v === '$250K – $500K') return price >= 250000 && price < 500000;
          if (v === '$500K – $750K') return price >= 500000 && price < 750000;
          if (v === '$750K – $1M') return price >= 750000 && price < 1000000;
          if (v === 'Over $1M') return price >= 1000000;
          return false;
        };
        if (Array.isArray(value)) {
          // Only match if at least one non-empty value matches the price range
          return value.filter(Boolean).some(match);
        }
        if (typeof value === 'string' && value !== '') {
          return match(value);
        }
        return true;
      },
    },
    {
      key: 'Bedrooms',
      label: 'Bedrooms',
      type: 'select',
      icon: EIcons.BED,
      options: [
        { label: '2+', value: '2' },
        { label: '3+', value: '3' },
        { label: '4+', value: '4' },
        { label: '5+', value: '5' },
      ],
      default: '',
      filterFn: (item, value) => {
        const beds = item.Plan?.Bedrooms ?? 0;
        if (!value) return true;
        return beds >= Number(value);
      },
    },
    {
      key: 'Stories',
      label: 'Stories',
      type: 'select',
      multi: true,
      icon: EIcons.STORIES,
      options: [
        { label: '1 Story', value: '1' },
        { label: '2 Story', value: '3' },
        { label: "2 Story Owner's Retreat Down", value: '4' },
        { label: "Dual Owner's Retreat", value: '7' },
      ],
      default: '',
      filterFn: (item, value) => {
        const storyIds =
          item.Plan?.Stories?.map((story) => String(story.Id)) ?? [];
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        if (Array.isArray(value)) {
          return value.filter(Boolean).some((v) => storyIds.includes(v));
        }
        if (typeof value === 'string' && value !== '') {
          return storyIds.includes(value);
        }
        return true;
      },
    },
    {
      key: 'SquareFeet',
      label: 'Square Footage',
      type: 'select',
      multi: true,
      icon: EIcons.AREA,
      options: [
        { label: 'Under 1000', value: 'Under 1000' },
        { label: '1000–2000', value: '1000–2000' },
        { label: '2000–3000', value: '2000–3000' },
        { label: '3000–4000', value: '3000–4000' },
        { label: '4000–5000', value: '4000–5000' },
        { label: 'Over 5000', value: 'Over 5000' },
      ],
      default: '',
      filterFn: (item, value) => {
        const sqft = item.SquareFeet ?? 0;
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        const match = (v: string) => {
          if (v === 'Under 1000') return sqft < 1000;
          if (v === '1000–2000') return sqft >= 1000 && sqft <= 2000;
          if (v === '2000–3000') return sqft >= 2000 && sqft <= 3000;
          if (v === '3000–4000') return sqft >= 3000 && sqft <= 4000;
          if (v === '4000–5000') return sqft >= 4000 && sqft <= 5000;
          if (v === 'Over 5000') return sqft > 5000;
          return false;
        };
        if (Array.isArray(value)) {
          return value.filter(Boolean).some(match);
        }
        if (typeof value === 'string' && value !== '') {
          return match(value);
        }
        return true;
      },
    },
    {
      key: 'IsMultiGen',
      label: 'Multigenerational Plan',
      type: 'checkbox',
      options: null,
      default: false,
      filterFn: (item, value) => !value || item.Plan.IsMultiGen === true,
    },
    {
      key: 'HasModelTour',
      label: 'Model Homes Available',
      type: 'checkbox',
      options: null,
      default: false,
      filterFn: (item, value) =>
        !value || !!item?.FloorPlans?.[0]?.HasModelHome,
    },
    {
      key: 'HasGarage',
      label: 'Garage Included',
      type: 'checkbox',
      options: null,
      default: false,
      filterFn: (item, value) => !value || item.Plan.HasGarage === true,
    },
    {
      key: 'MinDepth',
      label: 'Minimum',
      type: 'text',
      options: null,
      default: '',
      filterFn: (item, minVal) => {
        if (!minVal) return true;
        const depth = Number(item.Depth) || 0;
        return depth >= Number(minVal);
      },
    },
    {
      key: 'MaxDepth',
      label: 'Maximum',
      type: 'text',
      options: null,
      default: '',
      filterFn: (item, maxVal) => {
        if (!maxVal) return true;
        const depth = Number(item.Depth) || 0;
        return depth <= Number(maxVal);
      },
    },
    {
      key: 'MinWidth',
      label: 'Minimum',
      type: 'text',
      options: null,
      default: '',
      filterFn: (item, minVal) => {
        if (!minVal) return true;
        const width = Number(item.Width) || 0;
        return width >= Number(minVal);
      },
    },
    {
      key: 'MaxWidth',
      label: 'Maximum',
      type: 'text',
      options: null,
      default: '',
      filterFn: (item, maxVal) => {
        if (!maxVal) return true;
        const width = Number(item.Width) || 0;
        return width <= Number(maxVal);
      },
    },
  ];
}
